import React from 'react';
import I18n from 'i18n-js';

import FormGroupWithRadio from '@reverbdotcom/commons/src/components/form_group_with_radio';
import SiteModule from '@reverbdotcom/commons/src/components/site_module';

import { core_apimessages_ShippingLabelPackage } from '../../schemas/rql';
import CustomPackageInputs from '../discovery/shipping_labels/custom_package_inputs';
import { MeasurementSystem, buildNewPackage } from '../discovery/shipping_labels/shipping_label_package';

interface IProps {
  shippingPackage?: core_apimessages_ShippingLabelPackage;
  standardAmountUsd?: string;
  combineAndSaveAmountUsd?: string;
}

function CarrierCalculatedDescription() {
  return (
    <ul className="sell-form-package-inputs-container__list-group">
      <li className="sell-form-package-inputs-container__list-item">
        <strong>{I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculatedRate.description.guesswork')}</strong>
        {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculatedRate.description.reverbCalculates')}
      </li>
      <li className="sell-form-package-inputs-container__list-item">
        {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculatedRate.description.packageLabel')}
      </li>
    </ul>
  );
}

function RadioLabel(shippingType: 'carrierCalculated' | 'custom') {
  const forCarrierCalculated = shippingType === 'carrierCalculated';
  return (
    <label htmlFor={`${shippingType}ShippingRadio`}>
      <span className="mr-1 size-120 weight-normal">
        {I18n.t(`discovery.dashboard.selling.shippingPackageType.${shippingType}Rate.title`)}

        {forCarrierCalculated && (
          <span className="sell-form-package-inputs-container__title-emphasize">
            {I18n.t(`discovery.dashboard.selling.shippingPackageType.${shippingType}Rate.carrierSpecification`)}
          </span>
        )}
      </span>
    </label>
  );
}

function RadioHelpText(shippingType: 'carrierCalculated' | 'custom') {
  return (
    <p className="sell-form-package-inputs-container__help-text">
      {I18n.t(`discovery.dashboard.selling.shippingPackageType.${shippingType}Rate.helpText`)}
    </p>
  );
}

// This component will only be rendered in the sell form for USD/US_CON sellers, who are rolled out for carrier-calculated shipping.
// It allows eligible sellers to either submit package information, but otherwise replicates the default shipping price inputs on the current sell form.

export default function SellFormPackageInputsContainer(props: IProps) {
  const [inCarrierCalculatedMode, setCarrierCalculatedMode] = React.useState(!!props.shippingPackage);
  const [shippingPackage, setShippingPackage] = React.useState(props.shippingPackage || buildNewPackage(MeasurementSystem.IMPERIAL));
  const [standardAmountUsd, setStandardAmountUsd] = React.useState(props.standardAmountUsd || '');
  const [combineAndSaveAmountUsd, setCombineAndSaveAmountUsd] = React.useState(props.combineAndSaveAmountUsd || '');

  return (
    <>
      <div className="scaling-mb-2">
        <p><strong>{I18n.t('discovery.dashboard.selling.shippingPackageType.header')}</strong></p>
        <SiteModule>
          <FormGroupWithRadio
            inputName="customShippingRadio"
            label={RadioLabel('custom')}
            value={`${!inCarrierCalculatedMode}`}
            checked={!inCarrierCalculatedMode}
            helpText={RadioHelpText('custom')}
            updateField={() => setCarrierCalculatedMode(false)}
          />
        </SiteModule>
        <SiteModule footerContent={CarrierCalculatedDescription()}>
          <FormGroupWithRadio
            inputName="carrierCalculatedShippingRadio"
            label={RadioLabel('carrierCalculated')}
            value={`${inCarrierCalculatedMode}`}
            checked={inCarrierCalculatedMode}
            helpText={RadioHelpText('carrierCalculated')}
            updateField={() => setCarrierCalculatedMode(true)}
          />
        </SiteModule>
      </div>

      <input
        type="hidden"
        id="product_shipping_rates__region_type"
        name="product[shipping][rates][][region_type]"
        value="subregion"
        data-region-type="true"
      />
      <input
        type="hidden"
        id="product_shipping_rates__region_code"
        name="product[shipping][rates][][region_code]"
        value="US_CON"
        data-region-code="true"
      />

      {inCarrierCalculatedMode && (
        <>
          <CustomPackageInputs
            shippingPackage={shippingPackage}
            measurementSystem={MeasurementSystem.IMPERIAL}
            updatePackageDimensions={setShippingPackage}
            forCarrierCalculated
            hideMeasurementSystemControls
          />
          <input
            id="product_shipping_US_CON_rate_type"
            type="hidden"
            name="product[shipping][rates][][rate_type]"
            value="carrier_calculated"
          />
        </>
      )}

      {!inCarrierCalculatedMode && (
        <div className="shipping-rate-card__rates">
          {/* The `Standard Price` and `Combine & Save` form groups match form groups used in core */}
          {/* These form groups will move to react in a future iteration of the sell form */}
          {/* eslint-disable */}
          <div className="shipping-rate-card__rate">
            <div className="shipping-rate-card__rate__input">
              {/* Standad price form group */}
              <div className="form-group">
                <div className="form-group__header">
                  <label htmlFor="product_subregion_US_CON_standard">
                    {I18n.t('discovery.dashboard.selling.shippingRates.rateFields.standardRate')}
                  </label>
                </div>
                <div className="form-group__fields">
                  <div className="input-group">
                    <span className="input-group__text">
                      <span className="fa fa-usd"></span>
                    </span>
                    <input
                      id="product_subregion_US_CON_standard"
                      name="product[shipping][rates][][rate]"
                      type="text"
                      className="input-group__input required"
                      data-shipping-rate="true"
                      data-shipping-country-code="US_CON"
                      data-required-shipping-rate="true"
                      pattern="^[\d.,\s]+$"
                      required
                      value={standardAmountUsd}
                      onChange={event => setStandardAmountUsd(event.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Combine & Save price form group */}
              <div className="form-group">
                <div
                  className="form-group__header"
                  data-tooltip-text={`${I18n.t('discovery.dashboard.selling.shippingRates.rateFields.combinedShippingTooltip')} ${I18n.t('discovery.dashboard.selling.shippingRates.rateFields.validationErrors.greaterThanStandard')}`}
                >
                  <label className="icon-r-question-circle" htmlFor="product_subregion_US_CON_combined">
                    {I18n.t('discovery.dashboard.selling.shippingRates.rateFields.combinedRate')}
                  </label>
                  <div className="span label-tag ml-1">
                    {I18n.t('discovery.dashboard.selling.shippingRates.rateFields.optional')}
                  </div>
                </div>

                <div className="form-group__fields">
                  <div className="input-group">
                    <span className="input-group__text">
                      <span className="fa fa-usd"></span>
                    </span>
                    <input
                      type="text"
                      name="product[shipping][rates][][incremental_rate]"
                      id="product_subregion_US_CON_combined"
                      className="input-group__input"
                      data-shipping-rate="true"
                      data-shipping-country-code="US_CON"
                      pattern="^[\d.,\s]+$"
                      value={combineAndSaveAmountUsd}
                      onChange={event => setCombineAndSaveAmountUsd(event.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* eslint-enable */}
        </div>
      )}
    </>
  );
}
